import {
  collection,
  doc,
  getDoc,
  updateDoc,
  arrayUnion,
  Timestamp,
  DocumentReference,
  FieldValue,
  query,
  where,
  getDocs,
  setDoc,
  runTransaction,
  deleteField
} from "firebase/firestore";
import { firestore, auth } from "../firebase";
import { CollabLog } from "../types";
import { Influencer, HEART_REACTION } from "@foodfluence/shared";
import { RecaptchaVerifier, linkWithPhoneNumber, ConfirmationResult, sendSignInLinkToEmail, isSignInWithEmailLink, signInWithEmailLink, signInWithPhoneNumber  } from "firebase/auth";
import { generateInstagramCode, normalizeInstagramHandle } from "./verificationService";
import { getDownloadURL, getMetadata, ref, uploadBytes } from "firebase/storage";
import { storage } from "../firebase";
import { collabApi } from "./api/collab";


declare global {
  interface Window {
    recaptchaVerifier: RecaptchaVerifier | undefined;
    confirmationResult: ConfirmationResult | undefined;
  }
}

const updateCollabStatus = async (collabId: string, newStatus: string, additionalFields: Record<string, any> = {}): Promise<void> => {
  try {
    const collabRef: DocumentReference = doc(firestore, "collabs", collabId);
    const now: Timestamp = Timestamp.now();
    
    const updateData: {
      status: string;
      logs: CollabLog[] | FieldValue;
    } = {
      status: newStatus,
      logs: arrayUnion({
        status: newStatus,
        timestamp: now
      }),
      ...additionalFields
    };

    await updateDoc(collabRef, updateData);
    console.log(`Collab ${newStatus} successfully`);
  } catch (error) {
    console.error(`Error updating collab status to ${newStatus}:`, error);
    throw error;
  }
};

// rejectCollab / cancelCollab used to live here as direct Firestore writes. They now run
// server-side (collabApi.declineCollab / collabApi.cancelCollab) so the status change, the
// thread note, and the wallet release cannot come apart when a browser tab closes mid-flow.

export const redeemCollab = async (collabId: string): Promise<void> => {
  try {
    const now: Timestamp = Timestamp.now();
    await updateCollabStatus(collabId, "collabRedeemed", { collabRedeemedTime: now });
    console.log('Collab redeemed successfully');
  } catch (error) {
    console.error('Error redeeming collab:', error);
    throw error;
  }
};




export const checkInfluencerEmail = async (email: string): Promise<boolean> => {
  try {
    const influencersRef = collection(firestore, "influencers");
    const normalized = email.toLowerCase();
    // Deliberately no pendingEmail match. A pending address is one no Auth account held when it
    // was requested, so anyone can sign in with it here — and doing so mints a fresh Auth user
    // holding that address, which blocks the creator's own verify link for good. A creator
    // mid-change gets back in through their verify link, then /influencer/email/claim.
    const liveMatch = await getDocs(query(influencersRef, where("email_address", "==", normalized)));
    return !liveMatch.empty;
  } catch (error) {
    console.error("Error checking influencer email:", error);
    throw error;
  }
};

export const checkInfluencerPhone = async (phoneNumber: string): Promise<boolean> => {
  try {
    const influencersRef = collection(firestore, "influencers");
    const q = query(influencersRef, where("phoneNumber", "==", phoneNumber));
    const querySnapshot = await getDocs(q);
    return !querySnapshot.empty;
  } catch (error) {
    console.error("Error checking influencer phone:", error);
    throw error;
  }
};

export const sendEmailVerificationCode = async (email: string, phoneNumber: string, firstName: string, lastName: string): Promise<boolean> => {
  try {
    const actionCodeSettings = {
      url: `${window.location.origin}/collabs?email=${encodeURIComponent(email)}&phoneNumber=${encodeURIComponent(phoneNumber)}&firstName=${encodeURIComponent(firstName)}&lastName=${encodeURIComponent(lastName)}`,
      handleCodeInApp: true,
    };
    await sendSignInLinkToEmail(auth, email, actionCodeSettings);
    
    return true;
  } catch (error) {
    console.error("Error sending email verification code:", error);
    return false;
  }
};

// Reset (not clear) the shared verifier after a failed send. RecaptchaInitializer creates it once per
// page load, so clearing it left every retry failing with "reCAPTCHA not initialized" until a reload.
const resetRecaptcha = (): void => {
  window.recaptchaVerifier
    ?.render()
    .then((widgetId) => window.grecaptcha?.reset(widgetId))
    .catch((e) => console.error("Error resetting reCAPTCHA:", e));
};

export const sendPhoneVerificationCode = async (phoneNumber: string): Promise<any> => {
  try {
    if (!window.recaptchaVerifier) {
      throw new Error("reCAPTCHA not initialized");
    }
    const confirmationResult = await signInWithPhoneNumber(auth, phoneNumber, window.recaptchaVerifier);
    return confirmationResult;
  } catch (error) {
    console.error("Error sending phone verification code:", error);
    resetRecaptcha();
    throw error;
  }
};

export const sendLinkPhoneNumberCode = async (phoneNumber: string): Promise<any> => {
  try {
    if (!window.recaptchaVerifier) {
      throw new Error("reCAPTCHA not initialized");
    }
    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new Error("User must be signed in to link phone number");
    }
    const confirmationResult = await linkWithPhoneNumber(currentUser, phoneNumber, window.recaptchaVerifier);
    return confirmationResult;
  } catch (error) {
    console.error("Error sending phone verification code:", error);
    resetRecaptcha();
    throw error;
  }
};

export const verifyEmailCode = async (): Promise<boolean> => {
  try {
    const urlParams = new URLSearchParams(window.location.search);
    const email = urlParams.get("email");
    const phoneNumber = urlParams.get("phoneNumber");
    const firstName = urlParams.get("firstName");
    const lastName = urlParams.get("lastName");

    if (isSignInWithEmailLink(auth, window.location.href)) {
      await signInWithEmailLink(auth, email, window.location.href);

      if (phoneNumber) {
        const user = auth.currentUser;
        if (user) {
          const influencer = await getInfluencerByEmail(email);
          if (influencer) {
            await updateInfluencerContactInformation(influencer.id, firstName, lastName, phoneNumber);
            console.log('Contact information added to the database successfully');
          } else {
            console.error('No influencer found for the given email.');
          }
        }
      }
      return true;
    }
    return false;
  } catch (error) {
    console.error("Error verifying email code:", error);
    return false;
  }
};

export const verifyPhoneCode = async (confirmationResult: ConfirmationResult, code: string): Promise<boolean> => {
  try {
    if (!confirmationResult) {
      throw new Error("Confirmation result not found");
    }
   
    const credential = await confirmationResult.confirm(code);
    return !!credential.user;
  } catch (error) {
    console.error("Error verifying phone code:", error);
    return false;
  }
};

export const getInfluencerByPhoneNumber = async (phoneNumber: string): Promise<Influencer | null> => {
  try {
    const influencersRef = collection(firestore, "influencers");
    const q = query(influencersRef, where("phoneNumber", "==", phoneNumber));
    const querySnapshot = await getDocs(q);
    
    if (!querySnapshot.empty) {
      const influencerDoc = querySnapshot.docs[0];
      return { id: influencerDoc.id, ...influencerDoc.data() } as Influencer;
    }
    return null;
  } catch (error) {
    console.error("Error fetching influencer by phone number:", error);
    throw error;
  }
};

export const getInfluencerByEmail = async (email: string): Promise<Influencer | null> => {
  try {
    const influencersRef = collection(firestore, "influencers");
    const normalized = email.toLowerCase();

    const liveMatch = await getDocs(query(influencersRef, where("email_address", "==", normalized)));
    if (!liveMatch.empty) {
      const influencerDoc = liveMatch.docs[0];
      return { id: influencerDoc.id, ...influencerDoc.data() } as Influencer;
    }

    // Deliberately no pendingEmail fallback. A pending address is one no account held when it
    // was requested, so anyone can register it — matching on it here handed the whole creator
    // document, live over onSnapshot, to whoever controls that mailbox, which is the takeover
    // the backend refuses to allow in authorizeInfluencerAccess. It also hid the mid-change
    // state from the recovery path: a resolvable creator looks healthy, so nothing reconciles
    // and /email/sync answers 403 forever. Mid-change resolution goes through
    // POST /influencer/email/claim, which proves the address landed on the uid that asked for
    // it before committing. See InfluencerDataContext.
    return null;
  } catch (error) {
    console.error("Error fetching influencer by email:", error);
    throw error;
  }
};

export const getInfluencerByInstagramHandle = async (handle: string): Promise<Influencer | null> => {
  try {
    const cleanHandle = handle.replace('@', '').toLowerCase();
    const influencersRef = collection(firestore, "influencers");
    const q = query(influencersRef, where("instagramHandle", "==", cleanHandle));
    const querySnapshot = await getDocs(q);
    
    if (!querySnapshot.empty) {
      const influencerDoc = querySnapshot.docs[0];
      return { id: influencerDoc.id, ...influencerDoc.data() } as Influencer;
    }
    return null;
  } catch (error) {
    console.error("Error fetching influencer by Instagram handle:", error);
    throw error;
  }
};

export const getInfluencerByTikTokHandle = async (handle: string): Promise<Influencer | null> => {
  try {
    const cleanHandle = handle.replace('@', '').toLowerCase();
    const influencersRef = collection(firestore, "influencers");
    const q = query(influencersRef, where("tiktokHandle", "==", cleanHandle));
    const querySnapshot = await getDocs(q);
    
    if (!querySnapshot.empty) {
      const influencerDoc = querySnapshot.docs[0];
      return { id: influencerDoc.id, ...influencerDoc.data() } as Influencer;
    }
    return null;
  } catch (error) {
    console.error("Error fetching influencer by TikTok handle:", error);
    throw error;
  }
};

export const createOrRetrieveInstagramVerificationCode = async (email: string, rawHandle: string): Promise<string> => {
  try {
    const influencersRef = collection(firestore, "instagramVerifications");
    // Lowercase: the Instagram DM webhook looks the record up by the username the Graph API
    // returns, which Instagram always gives back lowercase. A record stored under mixed case is
    // never found, so the creator DMs the code and nothing happens — silently, forever.
    const instagramHandle = normalizeInstagramHandle(rawHandle);

    const existingCode = await doesRecentInstagramVerificationCodeExist(instagramHandle);

    if (!existingCode) {
      const code = generateInstagramCode();
      await setDoc(doc(influencersRef, instagramHandle), {
        email: email.toLowerCase(),
        instagramHandle: instagramHandle,
        verificationCode: code,
        createdAt: Timestamp.now(),
        verified: false
      });
      return code;
    }
    return existingCode;
  } catch (error) {
    console.error("Error storing Instagram verification code:", error);
    throw error;
  }
};

export const doesRecentInstagramVerificationCodeExist = async (instagramHandle: string): Promise<string> => {
  try {
    const influencersRef = doc(firestore, "instagramVerifications", normalizeInstagramHandle(instagramHandle));
    const docSnapshot = await getDoc(influencersRef);
    if (docSnapshot.exists()) {
      const data = docSnapshot.data();
      const createdAt = data.createdAt.toDate();
      const code = data.verificationCode;
      const isRecent = createdAt > new Date(Date.now() - 10 * 60 * 1000); // 10 minutes ago
      if (isRecent) {
        return code;
      }
    }
    return null;
  } catch (error) {
    console.error("Error retrieving Instagram verification code:", error);
    throw error;
  }
};

/** `clientID` scopes the brand's dashboard inbox query; a thread without it is invisible there. */
export const createNewConversation = async (collabId: string, clientID?: string): Promise<void> => {
  try {
    const conversationRef = doc(collection(firestore, "conversations"), collabId);
    const now: Timestamp = Timestamp.now();
    await setDoc(conversationRef, {
      ...(clientID ? { clientID } : {}),
      time_created: now,
      messages_log: {}
    });
    collabApi.sendWelcomeMessageFromBrand(collabId).catch(err => console.error('Error sending welcome message from brand:', err));
    console.log('New conversation created successfully');
  } catch (error) {
    console.error('Error creating new conversation:', error);
    throw error;
  }
};

export const sendFileMessage = async (
  conversationId: string,
  fileName: string,
  fileUrl: string
): Promise<string> => {
  try {
    const conversationRef = doc(collection(firestore, "conversations"), conversationId);
    const now: Timestamp = Timestamp.now();
    const messageId = doc(collection(conversationRef, "messages_log")).id;

    // Update the conversation document with the file message
    await updateDoc(conversationRef, {
      [`messages_log.${messageId}`]: {
        sender: "influencer",
        body: fileName,
        file_url: fileUrl, 
        time_sent: now,
        type: "file", 
      }
    });

    console.log('File message sent successfully');
    return messageId;
  } catch (error) {
    console.error('Error sending file message:', error);
    throw error;
  }
};

export const doesConversationExist = async (conversationId: string): Promise<boolean> => {
  try {
    const conversationRef = doc(collection(firestore, "conversations"), conversationId);
    const conversationDoc = await getDoc(conversationRef);
    return conversationDoc.exists();
  } catch (error) {
    console.error('Error checking conversation existence:', error);
    throw error;
  }
};

export const updateMessageReadTime = async (conversationId: string, messageId: string): Promise<void> => {
  try {
    const conversationRef = doc(collection(firestore, "conversations"), conversationId);
    const now: Timestamp = Timestamp.now();

    await updateDoc(conversationRef, {
      [`messages_log.${messageId}.time_read`]: now,
    });

    console.log('Message read time updated successfully');
  } catch (error) {
    console.error('Error updating message read time:', error);
    throw error;
  }
};

/**
 * Set or clear the creator's heart on a message. Transactional so a heart can't land on a message
 * the brand unsent a moment earlier: a dotted-path update there would recreate a fragment (just
 * `reactions`) under messages_log, which every reader of the thread would then try to render.
 */
export const setMessageHeart = async (conversationId: string, messageId: string, hearted: boolean): Promise<void> => {
  const conversationRef = doc(collection(firestore, "conversations"), conversationId);
  await runTransaction(firestore, async (tx) => {
    const snap = await tx.get(conversationRef);
    if (!snap.data()?.messages_log?.[messageId]) return;
    tx.update(conversationRef, {
      [`messages_log.${messageId}.reactions.influencer`]: hearted
        ? { emoji: HEART_REACTION, time: Timestamp.now() }
        : deleteField(),
    });
  });
};

export const updateInfluencerContactInformation = async (
  influencerId: string,
  firstName?: string,
  lastName?: string,
  phoneNumber?: string
): Promise<void> => {
  try {
    const influencerRef = doc(firestore, "influencers", influencerId);
    const updateData: Record<string, any> = {};

    if (firstName) {
      updateData.firstName = firstName;
    }
    if (lastName) {
      updateData.lastName = lastName;
    }
    if (phoneNumber) {
      updateData.phoneNumber = phoneNumber;
    }

    await updateDoc(influencerRef, updateData);
    console.log('Influencer contact information updated successfully');
  } catch (error) {
    console.error('Error updating influencer contact information:', error);
    throw error;
  }
};

/** null when no proof record exists at all — staff-created and "Verify Later" creators, not a failed proof. */
export const checkInstagramVerification = async (instagramHandle: string): Promise<boolean | null> => {
  try {
    const verificationRef = doc(firestore, "instagramVerifications", normalizeInstagramHandle(instagramHandle));
    const docSnapshot = await getDoc(verificationRef);
    if (docSnapshot.exists()) {
      return docSnapshot.data().verified === true;
    }
    return null;
  } catch (error) {
    console.error("Error checking Instagram verification:", error);
    throw error;
  }
};

export const uploadMediaKit = async (
  fileData: Uint8Array,
  fileType: string,
  fileName: string
) => {
  try {
    const randomKey = Math.random().toString(36).substring(2, 15);
    const storageRef = ref(storage, `Media-Kits/${randomKey}/${fileName}`);

    const fileBlob = new Blob([fileData], { type: fileType });
    await uploadBytes(storageRef, fileBlob);

    const fileURL = await getDownloadURL(storageRef);
    return fileURL;
  } catch (error) {
    console.error("Error uploading file:", error);
    throw error;
  }
};

export const getInspirationContentInfo = async (
  storagePath: string
): Promise<{ url: string; contentType: string }> => {
  const fileRef = ref(storage, storagePath);
  const [url, metadata] = await Promise.all([
    getDownloadURL(fileRef),
    getMetadata(fileRef),
  ]);
  return { url, contentType: metadata.contentType ?? 'application/octet-stream' };
};

export const getContentBriefFileUrl = async (clientId: string,  contentId: string, fileName: string): Promise<string> => {
  try {
    const filePath = `Content-Briefs/${clientId}/${contentId}/${fileName}`;
    const fileRef = ref(storage, filePath);
    const url = await getDownloadURL(fileRef);
    return url;
  } catch (error) {
    console.error('Error getting content brief file URL:', error);
    throw error;
  }
};

export const updateCollabExtentionRequest = async (collabId: string, isWaitlist: boolean, isExtension: boolean): Promise<void> => {
  try {
    const collabRef: DocumentReference = doc(firestore, "collabs", collabId);
    const updateData: { endDateExtensionRequestTime?: Timestamp, waitlistRequestTime?: Timestamp } = {};
    if (isExtension) {
      updateData.endDateExtensionRequestTime = Timestamp.now();
    }
    if (isWaitlist) {
      updateData.waitlistRequestTime = Timestamp.now();
    }
    await updateDoc(collabRef, updateData);
    console.log("Collab end date extension request time updated successfully");
  } catch (error) {
    console.error("Error updating collab end date extension request time:", error);
    throw error;
  }
};

